/**
 * Как показать соответствие «кейс аномалии → кейс во вкладке «Кейсы»».
 *
 * ТРИ СОСТОЯНИЯ, ТРИ РАЗНЫХ ОТВЕТА
 *  • `EXACT` — доказано ОДНО соответствие: показывается один номер, как и раньше;
 *  • `AMBIGUOUS` — доказано НЕСКОЛЬКО: показываются ВСЕ номера и их количество;
 *  • `NOT_FOUND` / `NOT_EVALUATED` / отказ слоя — не показывается ничего.
 *
 * ПОЧЕМУ У AMBIGUOUS НЕТ ПОБЕДИТЕЛЯ. Аудит 28.08.2026 (кейсы #2427 и #5723) доказал,
 * что несколько номеров возникают не от нечёткого поиска, а от ДУБЛИРОВАНИЯ строк в
 * исходной выгрузке: одна и та же экспортная отправка приходит двумя листами, поэтому
 * реестр хранит две записи на одну перевозку. Каждый кандидат прошёл ТОТ ЖЕ физический
 * ключ `phys_dev_v1` и ТУ ЖЕ валидацию, что и единственный кандидат статуса EXACT.
 * Источниковая линия происхождения способна указать, какую из дублирующих строк пронёс
 * пайплайн, — и это НЕ основание объявить её победителем: обе записи описывают одну и
 * ту же перевозку. Поэтому интерфейс показывает неоднозначность честно, а не выбирает.
 * См. ai_transit/v2/reports/CASES_2427_5723_FREELANCITY_CROSSWALK_AUDIT.md
 *
 * ЭТО НЕ «ПОХОЖИЕ КЕЙСЫ». Слово «похожие» занято рекомендательным слоем, работающим по
 * сходству. Здесь сходства нет вовсе: это записи реестра, совпавшие по физической
 * идентичности перевозки. Отсюда «совпадающие».
 *
 * МОДУЛЬ НИЧЕГО НЕ СОПОСТАВЛЯЕТ. Набор кандидатов приходит с сервера уже готовым;
 * фронт не обращается к снимку реестра и не пересопоставляет ничего сам.
 */
import { plural } from './anomaly-explanation'
import type { FreelanceCaseMapping } from './anomaly-cases-v3.types'

/** Сколько номеров показывается сразу; остальные — за «Показать все». */
export const FREELANCE_INLINE_LIMIT = 5

export type FreelanceMappingView =
  /** Ничего не показывать: NOT_FOUND, NOT_EVALUATED, не настроенная среда, отказ слоя. */
  | { kind: 'hidden' }
  /** Доказано одно соответствие — прежний вид, единственное число. */
  | { kind: 'single'; caseNumber: string }
  /** Доказано несколько — показываются все, без выбора победителя. */
  | { kind: 'multiple'; caseNumbers: string[]; total: number; inline: string[]; rest: string[] }

/**
 * Что именно рисовать по ответу API.
 *
 * Пустой или отсутствующий список кандидатов при AMBIGUOUS даёт `hidden`, а не пустую
 * шапку: показывать заголовок «Кейсы во вкладке «Кейсы»» без единого номера — значит
 * обещать данные, которых нет.
 */
export function freelanceMappingView(fc: FreelanceCaseMapping | undefined | null): FreelanceMappingView {
  if (!fc) return { kind: 'hidden' }
  if (fc.status === 'EXACT') {
    return fc.selectedCaseNumber ? { kind: 'single', caseNumber: fc.selectedCaseNumber } : { kind: 'hidden' }
  }
  if (fc.status === 'AMBIGUOUS') {
    // Порядок приходит с сервера и уже детерминирован; здесь он только фиксируется —
    // ни сортировки по баллу, ни «лучшего кандидата» тут нет и быть не может.
    const nums = (fc.candidateCaseNumbers ?? []).filter((v) => typeof v === 'string' && v.trim() !== '')
    if (nums.length === 0) return { kind: 'hidden' }
    if (nums.length === 1) return { kind: 'single', caseNumber: nums[0] }
    return {
      kind: 'multiple',
      caseNumbers: nums,
      total: nums.length,
      inline: nums.slice(0, FREELANCE_INLINE_LIMIT),
      rest: nums.slice(FREELANCE_INLINE_LIMIT),
    }
  }
  return { kind: 'hidden' }
}

/**
 * «Найдено 2 совпадающих кейса» / «Найдено 5 совпадающих кейсов» /
 * «Найден 21 совпадающий кейс».
 *
 * Склоняется и глагол, и определение, и существительное: русский счётный оборот меняет
 * все три, и «Найдено 21 совпадающих кейсов» читается как машинный перевод.
 */
export function matchingCasesLabel(n: number): string {
  const verb = plural(n, 'Найден', 'Найдено', 'Найдено')
  const adj = plural(n, 'совпадающий', 'совпадающих', 'совпадающих')
  const noun = plural(n, 'кейс', 'кейса', 'кейсов')
  return `${verb} ${n.toLocaleString('ru-RU')} ${adj} ${noun}`
}
